import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {Auth} from '../../network/auth';
import {AsyncState, RejectedState} from '../../models/async_state';
import {Status} from '../../enums/status';
import {RootState} from '../store';

interface State {
    token: string | null;
    onboardingCompleted: boolean;
    tokenRegistered: boolean;
}

export const exchangeEmailWithCode = createAsyncThunk(
    'exchangeToken/exchangeEmailWithCode',
    async (data: {email: string; code: string}) => {
        const result = await Auth.exchangeEmailWithCode(data.email, data.code);
        return result.data;
    },
);

export const facebookLogin = createAsyncThunk('exchangeToken/facebookLogin', async (facebookToken: string) => {
    const result = await Auth.facebookLogin(facebookToken);
    return result.data;
});

export const googleLogin = createAsyncThunk('exchangeToken/googleLogin', async (googleToken: string) => {
    const result = await Auth.googleLogin(googleToken);
    return result.data;
});

export const isAuthenticatedSelector = (state: RootState) => state.exchangeToken.token !== null;

const onTokenFetched = (token: string) => {
    localStorage.setItem('token', token);
};

export const exchangeTokenSlice = createSlice({
    name: 'exchangeToken',
    initialState: () => {
        const existingToken = localStorage.getItem('token');
        const initialState: AsyncState<State> = {
            status: Status.IDLE,
            token: existingToken,
            onboardingCompleted: false,
            tokenRegistered: existingToken !== null,
        };
        return initialState as AsyncState<State>;
    },
    reducers: {
        logout: (state) => {
            state.status = Status.IDLE;
            state.token = null;
            state.onboardingCompleted = false;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(exchangeEmailWithCode.pending, (state) => {
            state.status = Status.PENDING;
        });
        builder.addCase(exchangeEmailWithCode.fulfilled, (state, action) => {
            if (
                action.payload.login != null &&
                action.payload.login.token != null &&
                action.payload.login.onboarding_completed != null
            ) {
                state.status = Status.FULFILLED;
                state.token = action.payload.login.token;
                state.onboardingCompleted = action.payload.login.onboarding_completed;
            } else {
                state.status = Status.IDLE;
                state.token = null;
                state.onboardingCompleted = false;
            }
        });
        builder.addCase(exchangeEmailWithCode.rejected, (state, action) => {
            state.status = Status.REJECTED;
            (state as RejectedState<State>).error = action.error.message ?? 'Error';
        });
        builder.addCase(facebookLogin.pending, (state) => {
            state.status = Status.PENDING;
        });
        builder.addCase(facebookLogin.fulfilled, (state, action) => {
            if (action.payload?.facebookLogin != null && action.payload.facebookLogin.token != null) {
                onTokenFetched(action.payload.facebookLogin.token);
            }
            if (
                action.payload?.facebookLogin != null &&
                action.payload.facebookLogin.token != null &&
                action.payload.facebookLogin.onboarding_completed != null
            ) {
                state.status = Status.FULFILLED;
                state.token = action.payload.facebookLogin.token;
                state.onboardingCompleted = action.payload.facebookLogin.onboarding_completed;
            } else {
                state.status = Status.IDLE;
                state.token = null;
                state.onboardingCompleted = false;
            }
        });
        builder.addCase(facebookLogin.rejected, (state, action) => {
            state.status = Status.REJECTED;
            (state as RejectedState<State>).error = action.error.message ?? 'Error';
        });
        builder.addCase(googleLogin.pending, (state) => {
            state.status = Status.PENDING;
        });
        builder.addCase(googleLogin.fulfilled, (state, action) => {
            if (action.payload?.googleLogin != null && action.payload.googleLogin.token != null) {
                onTokenFetched(action.payload.googleLogin.token);
            }

            if (
                action.payload?.googleLogin != null &&
                action.payload.googleLogin.token != null &&
                action.payload.googleLogin.onboarding_completed != null
            ) {
                state.status = Status.FULFILLED;
                state.token = action.payload.googleLogin.token;
                state.onboardingCompleted = action.payload.googleLogin.onboarding_completed;
            } else {
                state.status = Status.IDLE;
                state.token = null;
                state.onboardingCompleted = false;
            }
        });
        builder.addCase(googleLogin.rejected, (state, action) => {
            state.status = Status.REJECTED;
            (state as RejectedState<State>).error = action.error.message ?? 'Error';
        });
    },
});
